import { defineStore } from 'pinia'
// import { useBubblesStore } from '~/stores/bubbles' // Remove unused import
import type { MovieBubble } from '~/stores/interfaces'
import { log } from '@/composables/useLogger' // Assuming log exists

// Helper function to safely extract year
function getYear(yearValue: number | null | undefined): number | null {
  return yearValue ?? null
}

export const useMovieStore = defineStore('movie', {
  state: () => ({
    searchQuery: '',
    selectedActor: 'All',
    selectedGenre: 'All',
    selectedDirector: 'All', // New filter
    selectedWatchedStatus: 'All' as 'All' | 'Watched' | 'Unwatched', // New filter
    minYear: null as number | null, // New filter
    maxYear: null as number | null, // New filter
    currentRandomMovie: null as MovieBubble | null,
    movies: [] as MovieBubble[],
  }),

  getters: {
    // Using this.movies directly as the source of truth for the view
    // movieBubbles(): MovieBubble[] { ... } // This getter might be redundant if state.movies is kept up-to-date

    filteredMovies(): MovieBubble[] {
      return this.movies // Use state.movies
        .filter((movie: MovieBubble) => {
          const matchesSearch = movie.title.toLowerCase().includes(this.searchQuery.toLowerCase())
          const matchesActor =
            this.selectedActor === 'All' ||
            (movie.actors && movie.actors.includes(this.selectedActor))
          const matchesGenre =
            this.selectedGenre === 'All' ||
            (movie.genre && movie.genre.includes(this.selectedGenre))
          const matchesDirector =
            this.selectedDirector === 'All' ||
            (movie.director && movie.director.includes(this.selectedDirector))
          const matchesWatchedStatus =
            this.selectedWatchedStatus === 'All' ||
            (this.selectedWatchedStatus === 'Watched' && movie.watched) ||
            (this.selectedWatchedStatus === 'Unwatched' && !movie.watched)

          const publicationYear = getYear(movie.year)
          const matchesMinYear =
            this.minYear === null || (publicationYear !== null && publicationYear >= this.minYear)
          const matchesMaxYear =
            this.maxYear === null || (publicationYear !== null && publicationYear <= this.maxYear)

          return (
            matchesSearch &&
            matchesActor &&
            matchesGenre &&
            matchesDirector &&
            matchesWatchedStatus &&
            matchesMinYear &&
            matchesMaxYear
          )
        })
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    },

    uniqueActors(): string[] {
      const actors = this.movies.flatMap((movie: MovieBubble) => movie.actors || [])
      return ['All', ...Array.from(new Set(actors)).sort()]
    },

    uniqueGenres(): string[] {
      const genres = this.movies.flatMap((movie: MovieBubble) => movie.genre || [])
      return ['All', ...Array.from(new Set(genres)).sort()]
    },

    uniqueDirectors(): string[] {
      const directors = this.movies.flatMap((movie: MovieBubble) => movie.director || [])
      return ['All', ...Array.from(new Set(directors)).sort()]
    },

    publicationYearRange(): { min: number | null; max: number | null } {
      let min: number | null = null
      let max: number | null = null
      this.movies.forEach((movie) => {
        const year = getYear(movie.year)
        if (year !== null) {
          if (min === null || year < min) min = year
          if (max === null || year > max) max = year
        }
      })
      return { min, max }
    },

    randomMovie(): MovieBubble | null {
      return this.currentRandomMovie
    },
  },

  actions: {
    setSearchQuery(query: string) {
      this.searchQuery = query
    },
    // Consolidated filter setter
    setFilters(filters: {
      actor?: string
      genre?: string
      director?: string
      watchedStatus?: 'All' | 'Watched' | 'Unwatched'
      minYear?: number | null
      maxYear?: number | null
    }) {
      if (filters.actor !== undefined) this.selectedActor = filters.actor
      if (filters.genre !== undefined) this.selectedGenre = filters.genre
      if (filters.director !== undefined) this.selectedDirector = filters.director
      if (filters.watchedStatus !== undefined) this.selectedWatchedStatus = filters.watchedStatus
      if (filters.minYear !== undefined) this.minYear = filters.minYear
      if (filters.maxYear !== undefined) this.maxYear = filters.maxYear
    },
    resetFilters() {
      this.searchQuery = ''
      this.selectedActor = 'All'
      this.selectedGenre = 'All'
      this.selectedDirector = 'All'
      this.selectedWatchedStatus = 'All'
      this.minYear = null
      this.maxYear = null
    },
    // Individual setters might still be useful
    setSelectedActor(actor: string) {
      this.selectedActor = actor
    },
    setSelectedGenre(genre: string) {
      this.selectedGenre = genre
    },
    // ... other getters/actions ...

    getActors(movie: MovieBubble): string {
      if (!movie.actors || movie.actors.length === 0) return 'No actors listed'
      return movie.actors.length > 2
        ? `${movie.actors[0]}, ${movie.actors[1]}, ...`
        : movie.actors.join(', ')
    },
    getGenres(movie: MovieBubble): string[] {
      return movie.genre || []
    },
    getRandomMovie() {
      if (this.movies.length === 0) {
        // Use state.movies
        this.currentRandomMovie = null
      } else {
        const randomIndex = Math.floor(Math.random() * this.movies.length)
        this.currentRandomMovie = this.movies[randomIndex] || null
      }
    },
    setMovies(movies: MovieBubble[]) {
      log(
        'MovieStore - Setting movies:',
        movies.map((m) => ({ uuid: m.uuid, watched: m.watched })) // Use watched directly
      )
      this.movies = movies
      // Fetch a random movie after setting movies
      this.getRandomMovie()
    },
    updateMovie(updatedMovie: MovieBubble) {
      log('MovieStore - Updating movie:', {
        uuid: updatedMovie.uuid,
        oldState: this.movies.find((m) => m.uuid === updatedMovie.uuid)?.watched,
        newState: updatedMovie.watched,
        oldMeta: this.movies.find((m) => m.uuid === updatedMovie.uuid)?.meta,
        newMeta: updatedMovie.meta,
      })
      // Create a new array to ensure reactivity
      this.movies = this.movies.map((movie) =>
        movie.uuid === updatedMovie.uuid ? { ...updatedMovie } : movie
      )
      // If the updated movie was the random one, update it too
      if (this.currentRandomMovie?.uuid === updatedMovie.uuid) {
        this.currentRandomMovie = updatedMovie
      }
    },
    removeMovie(uuid: string) {
      this.movies = this.movies.filter((movie) => movie.uuid !== uuid)
      // Optionally, if the removed movie was the random one, pick a new one
      if (this.currentRandomMovie?.uuid === uuid) {
        this.getRandomMovie()
      }
    },
  },
})
